import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
// Import SEO component and Helmet
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async'; 

// Placeholder for translation strings
const translations = {
  en: {
    pageTitle: "Global Big Day at Jardim da Amazônia",
    days: "Days",
    hours: "Hours",
    minutes: "Minutes",
    seconds: "Seconds",
    immersionTitle: "A true bird‑watching immersion with students from the São José do Rio Claro Agricultural School",
    immersionText: "Guided by Prof. Dalci Oliveira—biologist, birder, and environmental consultant—the students began their journey at dawn, exploring the forest of Jardim da Amazônia Lodge. Over the course of the day, more than 170 bird species were recorded at this single location, including the rare ",
    pipileLink: "Pipile cujubi",
    immersionTextEnd: "! This impressive diversity was also documented in a parallel expedition led by Jhonatan Araújo, Rafael Lopes, and Rodrigo Barboza. In addition to learning visual and auditory identification techniques, the group reflected on the role of citizen science in conserving our ecosystems.",
    connectionTitle: "Local Connection with Global Movement",
    connectionText: "The event links our local work to a global movement that, in this edition, involved almost 1 million observers worldwide. Check out all the records on the Jardim da Amazônia profile on eBird.",
    callToActionTitle: "Join us on the next adventure!",
    callToActionText: "Follow @jardimdaamazonia to see more images, future events and conservation news.",
    instagramHandle: "@jardimdaamazonia",
    timeSensitive: "Time-Sensitive Event Page",
    toggleSound: "Toggle bird sounds",
    visitEbird: "Visit our eBird hotspot"
  },
  pt: {
    pageTitle: "Global Big Day no Jardim da Amazônia",
    days: "Dias",
    hours: "Horas",
    minutes: "Minutos",
    seconds: "Segundos",
    immersionTitle: "Uma verdadeira imersão na observação de aves com estudantes da Escola Agrícola de São José do Rio Claro",
    immersionText: "Guiados pelo Prof. Dalci Oliveira – biólogo, observador de aves e consultor ambiental – os estudantes iniciaram a jornada logo ao amanhecer, explorando a mata do Jardim da Amazônia Lodge. Ao longo do dia, foram registradas mais de 170 espécies de aves apenas nesta localidade, incluindo o raro ",
    pipileLink: "Pipile cujubi",
    immersionTextEnd: "! Essa impressionante diversidade também foi documentada em uma expedição paralela conduzida por Jhonatan Araújo, Rafael Lopes e Rodrigo Barboza. Além de aprender técnicas de identificação visual e auditiva, a turma refletiu sobre a importância da ciência cidadã para a conservação dos nossos ecossistemas.",
    connectionTitle: "Conexão local com o movimento global",
    connectionText: "O evento conecta o nosso trabalho local a um movimento global que, nesta edição, envolveu quase 1 milhão de observadores em todo o planeta. Confira todos os registros no perfil do Jardim da Amazônia no eBird.",
    callToActionTitle: "Junte‑se a nós na próxima aventura!",
    callToActionText: "Siga @jardimdaamazonia para ver mais imagens, eventos futuros e novidades sobre conservação.",
    instagramHandle: "@jardimdaamazonia",
    timeSensitive: "Página de Evento Sensível ao Tempo",
    toggleSound: "Alternar sons de pássaros",
    visitEbird: "Visite nosso hotspot no eBird"
  }
};

const GlobalBigDayPromo = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0];
  const texts = translations[currentLang] || translations.en;

  // Enhanced SEO Content
  const seoTitle = currentLang === 'pt' 
    ? "Global Big Day 2025 no Jardim da Amazônia | Observação de Aves e Ciência Cidadã"
    : "Global Big Day 2025 at Jardim da Amazônia | Birdwatching and Citizen Science";

  const seoDescription = currentLang === 'pt'
    ? "Relato do Global Big Day 2025 no Jardim da Amazônia: mais de 170 espécies registradas, incluindo o raro Pipile cujubi. Uma experiência única de observação de aves com estudantes da Escola Agrícola de São José do Rio Claro."
    : "Global Big Day 2025 at Jardim da Amazônia: over 170 species recorded, including the rare Pipile cujubi. A unique birdwatching experience with students from São José do Rio Claro Agricultural School.";

  const seoKeywords = "Global Big Day 2025, Jardim da Amazônia, birdwatching, Amazon birding, citizen science, Pipile cujubi, Mato Grosso birds, eBird hotspot, São José do Rio Claro, Prof. Dalci Oliveira";

  const seoImage = "https://res.cloudinary.com/dxlhv2mji/image/upload/v1738450243/tie_bicudo_jh_hfnk4c.jpg";
  const pageUrl = "https://www.jardimdaamazonia.com/global-big-day";

  // Enhanced Event Schema
  const eventSchema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: texts.pageTitle,
    description: seoDescription,
    startDate: "2025-05-10T00:00:00-04:00",
    endDate: "2025-05-10T23:59:59-04:00",
    eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
    eventStatus: "https://schema.org/EventCompleted",
    location: {
      '@type': 'Place',
      name: "Jardim da Amazônia Eco Lodge",
      address: {
        '@type': 'PostalAddress',
        addressLocality: "São José do Rio Claro",
        addressRegion: "MT",
        addressCountry: "BR"
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: -13.2697,
        longitude: -56.7179
      }
    },
    image: seoImage,
    organizer: {
      '@type': 'Organization',
      name: "Jardim da Amazônia Eco Lodge",
      url: "https://www.jardimdaamazonia.com"
    },
    performer: {
      '@type': 'Person',
      name: "Prof. Dalci Oliveira",
      jobTitle: "Biologist and Environmental Consultant"
    },
    offers: {
      '@type': 'Offer',
      availability: "https://schema.org/SoldOut",
      price: "0",
      priceCurrency: "BRL",
      validFrom: "2025-05-10T00:00:00-04:00"
    },
    url: pageUrl
  };

  // Add Article Schema
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: texts.pageTitle,
    description: seoDescription,
    image: seoImage,
    datePublished: "2025-05-10T00:00:00-04:00",
    dateModified: "2025-05-10T23:59:59-04:00",
    author: {
      '@type': 'Organization',
      name: "Jardim da Amazônia Eco Lodge",
      url: "https://www.jardimdaamazonia.com"
    },
    publisher: {
      '@type': 'Organization',
      name: "Jardim da Amazônia Eco Lodge",
      logo: {
        '@type': 'ImageObject',
        url: "https://www.jardimdaamazonia.com/logo.png"
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl
    }
  };

  return (
    <>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={seoImage}
        canonicalUrl={pageUrl}
      />
      <Helmet>
        {/* Event Schema */}
        <script type="application/ld+json">
          {JSON.stringify(eventSchema)}
        </script>
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        {/* Language Alternates */}
        <link rel="alternate" hrefLang="en" href="https://www.jardimdaamazonia.com/en/global-big-day" />
        <link rel="alternate" hrefLang="pt" href="https://www.jardimdaamazonia.com/pt/global-big-day" />
        <link rel="alternate" hrefLang="x-default" href="https://www.jardimdaamazonia.com/global-big-day" />
        {/* Open Graph Tags */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
      </Helmet>

      <div className="flex flex-col min-h-screen bg-background-color text-text-color">
        <div className="flex-grow container mx-auto px-4 py-8 md:py-16">
          <header className="text-center mb-12 md:mb-16">
            <h1 className="mt-8 md:mt-12 text-4xl md:text-5xl font-bold font-montserrat text-primary-color mb-4">
              {texts.pageTitle}
            </h1>
          </header>

          {/* Bilingual Content Sections */}
          <article className="space-y-12">
            {/* Immersion Section */}
            <section className="bg-base-100 rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col md:flex-row items-stretch">
              <div className="w-full md:w-1/3 flex-shrink-0">
                <img
                  src="/gbd-1.jpeg"
                  alt="Students birdwatching at Jardim da Amazônia"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="w-full md:w-2/3 p-6 flex flex-col justify-center">
                <h2 className="text-2xl font-semibold text-primary-color mb-4">{texts.immersionTitle}</h2>
                <p className="text-text-secondary text-justify">
                  {texts.immersionText}
                  <a 
                    href="https://ebird.org/species/rtpgua1/L968651" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-accent-color hover:text-accent-dark font-semibold"
                  >
                    {texts.pipileLink}
                  </a>
                  {texts.immersionTextEnd}
                </p>
              </div>
            </section>

            {/* Connection Section */}
            <section className="bg-base-100 rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col md:flex-row-reverse items-stretch">
              <div className="w-full md:w-1/3 flex-shrink-0">
                <img
                  src="/gbd-2.jpeg"
                  alt="Bird species at Jardim da Amazônia"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="w-full md:w-2/3 p-6 flex flex-col justify-center">
                <h2 className="text-2xl font-semibold text-primary-color mb-4">{texts.connectionTitle}</h2>
                <p className="text-text-secondary text-justify">{texts.connectionText}</p>
                <div className="mt-4">
                  <a 
                    href="https://ebird.org/hotspot/L968651" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-accent-color hover:text-accent-dark"
                  >
                    <span className="text-lg font-semibold">{texts.visitEbird}</span>
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </div>
            </section>

            {/* Call to Action Section */}
            <section className="bg-base-100 rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col md:flex-row items-stretch">
              <div className="w-full md:w-1/3 flex-shrink-0">
                <img
                  src="/gbd-3.jpeg"
                  alt="Jardim da Amazônia landscape"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="w-full md:w-2/3 p-6 flex flex-col justify-center">
                <h2 className="text-2xl font-semibold text-primary-color mb-4">{texts.callToActionTitle}</h2>
                <p className="text-text-secondary text-justify">{texts.callToActionText}</p>
                <div className="mt-4">
                  <a 
                    href="https://instagram.com/jardimdaamazonia" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-accent-color hover:text-accent-dark"
                  >
                    <span className="text-xl font-semibold">{texts.instagramHandle}</span>
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default GlobalBigDayPromo;
